import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalFooter, Button } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { Form, useField } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-focus';
import FormFieldsGenerator from '../../../../../../../shared/FormFieldsGenerator';
import { setDefaultValues } from '../../../../../../../utils/utils';

// Create focus decorator for automatic focus on validation errors
const focusOnError = createDecorator();

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  template: PropTypes.object.isRequired,
  controlledVocab: PropTypes.object.isRequired,
  currentHoldingsCount: PropTypes.number.isRequired,
  isProcessing: PropTypes.bool,
  instanceStatisticalCodeIds: PropTypes.array,
};

function CreateSingleHoldingModal({
  isOpen,
  onClose,
  onConfirm,
  template,
  controlledVocab,
  currentHoldingsCount,
  isProcessing = false,
  instanceStatisticalCodeIds = [],
}) {
  // Get classification data from instance for call number generation
  const classificationField = useField('instanceData.classifications');

  // Create initial holding data based on template and classification
  const createInitialHoldingData = useCallback(() => {
    // Get template defaults for holdings
    const templateDefaults = template?.templateFields?.holdings
      ? setDefaultValues(template.templateFields.holdings)
      : {};

    // Extract classification data for call number generation
    const classification = classificationField?.input?.value?.[0];
    const classificationNumber = classification?.classificationNumber || '';
    const authorNumber = classification?.authorNumber
      ? ` ${classification.authorNumber}`
      : '';

    // Generate call number from classification
    const callNumber =
      classificationNumber && authorNumber
        ? `${classificationNumber}${authorNumber}`
        : classificationNumber || '';

    // Combine template defaults with auto-generated call number and copy number
    const holdingData = {
      id: '', // Will be generated
      ...templateDefaults,
      // Override with auto-generated call number if available
      ...(callNumber && { callNumber }),
      // Propagate statistical codes from instance
      ...(instanceStatisticalCodeIds.length && { holdingStatisticalCodeIds: instanceStatisticalCodeIds }),
      // Auto-generate holding copy number
      holdingCopyNumber: currentHoldingsCount + 1,
      // Keep location empty for user selection unless template specifies it
      permanentLocationId: templateDefaults.permanentLocationId || '',
    };

    return holdingData;
  }, [template, classificationField?.input?.value, currentHoldingsCount]);

  const handleSubmit = useCallback(
    (values) => {
      // Prepare holding data for creation
      const holdingData = {
        ...createInitialHoldingData(),
        ...values,
        id: `new-holding-${Date.now()}`,
        // Ensure copy number is included
        holdingCopyNumber: values.holdingCopyNumber || currentHoldingsCount + 1,
      };

      onConfirm(holdingData);
      onClose();
      return undefined;
    },
    [createInitialHoldingData, onConfirm, onClose, currentHoldingsCount],
  );

  const validate = useCallback((values) => {
    const errors = {};

    // Basic validation - more specific validation will be handled by FormFieldsGenerator
    // This is mainly to catch any critical empty fields
    if (values) {
      // FormFieldsGenerator will handle field-specific validation based on template requirements
      Object.keys(values).forEach((key) => {
        if (typeof values[key] === 'string' && values[key].trim() === '') {
          // Let FormFieldsGenerator handle required field validation
        }
      });
    }

    return errors;
  }, []);

  const initialValues = createInitialHoldingData();

  return (
    <Form
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators }}
      decorators={[focusOnError]}
      initialValues={initialValues}
      validate={validate}
      render={(formRenderProps) => {
        const { handleSubmit: formSubmit, submitting } = formRenderProps;

        // Create footer JSX without memoization to avoid hooks rule violation
        const renderFooter = (
          <ModalFooter>
            <Button
              buttonStyle="primary"
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                formSubmit();
              }}
              disabled={submitting || isProcessing}
              marginBottom0
            >
              {isProcessing ? (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createSingleHoldingModal.creating"
                  defaultMessage="Creating Holding..."
                />
              ) : (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.createSingleHoldingModal.create"
                  defaultMessage="Create Holding"
                />
              )}
            </Button>
            <Button
              buttonStyle="default"
              onClick={onClose}
              disabled={isProcessing}
              marginBottom0
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.createSingleHoldingModal.cancel"
                defaultMessage="Cancel"
              />
            </Button>
          </ModalFooter>
        );

        return (
          <Modal
            open={isOpen}
            onClose={onClose}
            footer={renderFooter}
            wrappingElement="form"
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.createSingleHoldingModal.title"
                defaultMessage="Create New Holding"
              />
            }
            size="large"
          >
            <form onSubmit={formSubmit}>
              <div style={{ padding: '16px' }}>
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6c757d',
                  }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.createSingleHoldingModal.description"
                    defaultMessage="Fill in the holding details below. Required fields are marked with an asterisk."
                  />
                </div>

                {/* Generate form fields based on template */}
                {template?.templateFields?.holdings && (
                  <FormFieldsGenerator
                    key="create-holding-template-fields"
                    formValues={template?.templateFields?.holdings || []}
                    formRenderProps={formRenderProps}
                    pageName="" // Empty pageName so fields use direct field names
                    controlledVocab={controlledVocab}
                  />
                )}

                {/* Information section */}
                <div
                  style={{
                    marginTop: '24px',
                    padding: '16px',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '6px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '8px',
                      color: '#1976d2',
                    }}
                  >
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.createSingleHoldingModal.info.title"
                      defaultMessage="What happens after creation:"
                    />
                  </div>
                  <ul
                    style={{ margin: 0, paddingLeft: '20px', color: '#0c4a6e' }}
                  >
                    <li style={{ marginBottom: '4px' }}>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.createSingleHoldingModal.info.newAccordion"
                        defaultMessage="A new holding accordion will be added to the list"
                      />
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.createSingleHoldingModal.info.itemCreation"
                        defaultMessage="You can start adding items to this holding immediately"
                      />
                    </li>
                    <li style={{ marginBottom: '4px' }}>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.createSingleHoldingModal.info.editLater"
                        defaultMessage="All holding details can be edited later using the holding actions"
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </form>
          </Modal>
        );
      }}
    />
  );
}

CreateSingleHoldingModal.propTypes = propTypes;

export default CreateSingleHoldingModal;
